//  components\krajina\KrajinaKlient.tsx

"use client";

import { Flag } from "@/components/shared/flags";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Shield,
  User,
  Target,
  ChevronLeft,
  Info,
  Activity,
  History,
  Trophy,
  Users,
  Briefcase,
  MapPin,
  Scale,
  Ruler,
  Globe,
  X,
  Calendar,
} from "lucide-react";
import Link from "next/link";
import { motion } from "framer-motion";
import type { HracKomplet, Krajina, Zapas } from "@/types/database";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  DialogClose,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface ZapasSDatami extends Zapas {
  domaciKrajina: Krajina | null;
  hostiKrajina: Krajina | null;
}

interface KrajinaKlientProps {
  krajina: Krajina;
  nominacia: HracKomplet[];
  aktivnehlasovanie_id: string | null;
  zapasy?: ZapasSDatami[];
}

const INFO_BOX_CLASSES = cn(
  "p-4 rounded-xl bg-muted/30 border border-muted/20 transition-all duration-300",
  "hover:bg-muted/40 hover:shadow-sm",
);
const STAT_CARD_CLASSES = cn(
  "p-4 rounded-xl bg-muted/30 border border-muted/20 text-center transition-all duration-300",
  "hover:bg-muted/40 hover:shadow-md hover:-translate-y-1",
);
const EMPTY_STATE_CLASSES = cn(
  "border-muted/20 shadow-lg text-center py-12 md:py-24 px-6 rounded-3xl backdrop-blur-md",
  "bg-background/50 border-dashed",
);

export function KrajinaKlient({
  krajina,
  nominacia,
  aktivnehlasovanie_id,
  zapasy = [],
}: KrajinaKlientProps) {
  const SITE_TITLE = "ZOH 2026";
  const NOMINATION_DESC =
    "Oficiálna nominácia na hokejový turnaj ZOH 2026 v Miláne.";
  const PLAYER_DETAIL_TEXT = "Detail hráča";
  const NOMINATION_PREPARING_TITLE = "Nominácia sa pripravuje";
  const VOTE_FOR_PLAYER_TEXT = "Hlasovať za hráča";
  const BACK_TO_GROUPS_TEXT = "Späť na skupiny";
  const VIEW_OTHER_TEAMS_TEXT = "Prezrieť ostatné tímy";
  const TEAM_INFO_TITLE = "Informácie o tíme";
  const COACH_TEXT = "Hlavný tréner";
  const ASSISTANTS_TEXT = "Asistenti";
  const GM_TEXT = "Generálny manažér";
  const RANKING_TEXT = "IIHF Ranking";
  const LAST_MS_TEXT = "Posledné MS";
  const FORM_TEXT = "Aktuálna forma";

  const getPoziciaIcon = (pozicia: string) => {
    switch (pozicia) {
      case "brankar":
        return <Shield className="h-4 w-4" />;
      case "obranca":
        return <User className="h-4 w-4" />;
      case "utocnik":
        return <Target className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getPoziciaLabel = (pozicia: string) => {
    switch (pozicia) {
      case "brankar":
        return "Brankár";
      case "obranca":
        return "Obranca";
      case "utocnik":
        return "Útočník";
      default:
        return pozicia;
    }
  };

  const hraciPodlaPozicie = {
    brankar: nominacia.filter((h) => h.pozicia === "brankar"),
    obranca: nominacia.filter((h) => h.pozicia === "obranca"),
    utocnik: nominacia.filter((h) => h.pozicia === "utocnik"),
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <div className="flex flex-col min-h-screen">
      <div className="container mx-auto px-4 py-8 md:px-8 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="mb-6 -ml-2 text-muted-foreground hover:text-foreground rounded-full"
          >
            <Link href="/skupiny">
              <ChevronLeft className="h-4 w-4 mr-1" />
              {BACK_TO_GROUPS_TEXT}
            </Link>
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row items-center gap-8 mb-12"
        >
          <motion.div
            whileHover={{ scale: 1.05, rotate: 2 }}
            className="p-2 bg-background border border-muted/20 shadow-2xl rounded-3xl overflow-hidden shrink-0 backdrop-blur-sm"
          >
            <Flag kod={krajina.kod} size={140} />
          </motion.div>
          <div className="text-center md:text-left space-y-3">
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mb-1">
              <Badge
                variant="outline"
                className="rounded-full bg-primary/5 border-primary/20 text-primary font-black uppercase tracking-widest text-[10px] py-1 px-3"
              >
                ZOH 2026 MILÁNO
              </Badge>
              {krajina.iihf_rebricek && (
                <Badge
                  variant="outline"
                  className="rounded-full bg-muted/30 border-muted/20 text-muted-foreground font-bold uppercase tracking-tighter text-[10px] py-1 px-3"
                >
                  {RANKING_TEXT}: #{krajina.iihf_rebricek}
                </Badge>
              )}
            </div>
            <h1 className="text-4xl md:text-6xl font-black tracking-tighter mb-2 uppercase drop-shadow-sm">
              {krajina.nazov}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground/80 font-medium max-w-2xl">
              {NOMINATION_DESC}
            </p>
          </div>
        </motion.div>

        {/* Tímové informácie */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
        >
          <Card className="md:col-span-2 border-muted/20 shadow-sm bg-transparent rounded-3xl overflow-hidden">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2 text-primary">
                <Globe className="w-5 h-5" />
                <CardTitle className="text-xl font-black uppercase tracking-tight">
                  {TEAM_INFO_TITLE}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {krajina.hlavny_trener && (
                  <div className="flex items-center gap-3 p-3 bg-muted/30 border border-muted/20 rounded-xl">
                    <div className="p-2 bg-muted rounded-full text-primary">
                      <User className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-muted-foreground/60 uppercase tracking-widest leading-tight">
                        {COACH_TEXT}
                      </p>
                      <p className="text-sm font-bold">
                        {krajina.hlavny_trener}
                      </p>
                    </div>
                  </div>
                )}
                {krajina.generalny_manazer && (
                  <div className="flex items-center gap-3 p-3 bg-muted/30 border border-muted/20 rounded-xl">
                    <div className="p-2 bg-muted rounded-full text-primary">
                      <Briefcase className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-muted-foreground/60 uppercase tracking-widest leading-tight">
                        {GM_TEXT}
                      </p>
                      <p className="text-sm font-bold">
                        {krajina.generalny_manazer}
                      </p>
                    </div>
                  </div>
                )}
                {krajina.asistenti && krajina.asistenti.length > 0 && (
                  <div className="flex items-start gap-3 p-3 bg-muted/30 border border-muted/20 rounded-xl sm:col-span-2">
                    <div className="p-2 bg-muted rounded-full text-primary shrink-0">
                      <Users className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-muted-foreground/60 uppercase tracking-widest leading-tight">
                        {ASSISTANTS_TEXT}
                      </p>
                      <p className="text-sm font-bold">
                        {krajina.asistenti.join(", ")}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {krajina.vysledky_posledny_ms && (
                <div className="p-4 bg-primary/5 border border-primary/10 rounded-2xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-4 h-4 text-primary" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-primary/80">
                      {LAST_MS_TEXT}
                    </span>
                  </div>
                  <p className="text-sm font-bold text-foreground/90">
                    {krajina.vysledky_posledny_ms}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {krajina.forma_posledna_sezona && (
            <Card className="border-muted/20 shadow-sm bg-transparent rounded-3xl overflow-hidden flex flex-col">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 text-primary">
                  <Activity className="w-5 h-5" />
                  <CardTitle className="text-xl font-black uppercase tracking-tight">
                    {FORM_TEXT}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="flex-1">
                <p className="text-sm text-muted-foreground/90 leading-relaxed font-medium italic">
                  &quot;{krajina.forma_posledna_sezona}&quot;
                </p>
              </CardContent>
            </Card>
          )}
        </motion.div>

        {/* Zápasy tímu */}
        {zapasy.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-16"
          >
            <div className="flex items-center gap-4 border-b border-muted/20 pb-4 mb-8">
              <div className="p-3 bg-primary/10 rounded-xl text-primary shadow-inner">
                <Calendar className="h-5 w-5" />
              </div>
              <h2 className="text-2xl md:text-3xl font-black tracking-tight uppercase">
                Zápasy na ZOH 2026
              </h2>
              <Badge
                variant="secondary"
                className="ml-auto rounded-full px-4 py-1 text-xs font-black uppercase tracking-widest bg-muted/50 border border-muted/20"
              >
                {zapasy.length} zápasov
              </Badge>
            </div>

            <div className="grid gap-4">
              {zapasy.map((z) => {
                const jeUkonceny = z.stav === "ukonceny";
                const domaciKod = z.domaciKrajina?.kod || "TBD";
                const hostiKod = z.hostiKrajina?.kod || "TBD";
                const domaciNazov = z.domaciKrajina?.nazov || "TBD";
                const hostiNazov = z.hostiKrajina?.nazov || "TBD";
                const datumObj = new Date(z.datum_cas);

                return (
                  <Link key={z.id} href={`/zapas/${z.id}`} className="block">
                    <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 border-muted/20 bg-background/50 backdrop-blur-sm cursor-pointer">
                      <CardContent className="p-4 md:p-6">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 flex-1 min-w-0 justify-end">
                            <span
                              className={cn(
                                "text-sm md:text-base font-black uppercase tracking-tight truncate",
                                domaciKod === krajina.kod
                                  ? "text-primary"
                                  : "text-foreground",
                              )}
                            >
                              {domaciNazov}
                            </span>
                            <Flag kod={domaciKod} size={28} />
                          </div>

                          <div className="flex flex-col items-center gap-1 px-4 min-w-[80px]">
                            {jeUkonceny ? (
                              <div className="text-2xl md:text-3xl font-black tracking-tighter tabular-nums">
                                {z.skore_domaci}
                                <span className="text-muted-foreground/30 mx-1">
                                  :
                                </span>
                                {z.skore_hosti}
                              </div>
                            ) : (
                              <div className="text-sm font-black text-muted-foreground/40">
                                VS
                              </div>
                            )}
                            <Badge
                              variant="outline"
                              className={cn(
                                "rounded-full px-3 py-0.5 text-[9px] font-black uppercase tracking-widest",
                                jeUkonceny
                                  ? "bg-status-success/10 text-status-success border-status-success/20"
                                  : "bg-muted/30 text-muted-foreground border-muted/20",
                              )}
                            >
                              {jeUkonceny
                                ? "Koniec"
                                : datumObj.toLocaleDateString("sk-SK", {
                                    day: "numeric",
                                    month: "numeric",
                                  })}
                            </Badge>
                          </div>

                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <Flag kod={hostiKod} size={28} />
                            <span
                              className={cn(
                                "text-sm md:text-base font-black uppercase tracking-tight truncate",
                                hostiKod === krajina.kod
                                  ? "text-primary"
                                  : "text-foreground",
                              )}
                            >
                              {hostiNazov}
                            </span>
                          </div>
                        </div>

                        {z.miesto && (
                          <div className="mt-3 pt-3 border-t border-muted/10 flex justify-center items-center gap-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                            <MapPin className="h-3 w-3 text-primary/60" />
                            <span className="truncate">{z.miesto}</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}

        {nominacia.length > 0 ? (
          <div className="space-y-12">
            {(
              Object.keys(hraciPodlaPozicie) as Array<
                keyof typeof hraciPodlaPozicie
              >
            ).map((pozicia, idx) => (
              <motion.section
                key={pozicia}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="space-y-8"
              >
                <div className="flex items-center gap-4 border-b border-muted/20 pb-4">
                  <div className="p-3 bg-primary/10 rounded-xl text-primary shadow-inner">
                    {getPoziciaIcon(pozicia)}
                  </div>
                  <h2 className="text-2xl md:text-3xl font-black tracking-tight uppercase">
                    {getPoziciaLabel(pozicia)}
                  </h2>
                  <Badge
                    variant="secondary"
                    className="ml-auto rounded-full px-4 py-1 text-xs font-black uppercase tracking-widest bg-muted/50 border border-muted/20"
                  >
                    {hraciPodlaPozicie[pozicia].length} mien
                  </Badge>
                </div>

                <motion.div
                  variants={container}
                  initial="hidden"
                  animate="show"
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                >
                  {hraciPodlaPozicie[pozicia].map((hrac) => {
                    const cisloDresu = hrac.cisla_dresov
                      ? hrac.cisla_dresov.split(",")[0].trim()
                      : null;
                    return (
                      <motion.div
                        key={hrac.id}
                        variants={item}
                        whileHover={{ scale: 1.02, y: -4 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Dialog>
                          <DialogTrigger asChild>
                            <Card className="border-muted/20 shadow-sm hover:shadow-xl transition-all h-full cursor-pointer hover:border-primary/30 group bg-background/50 backdrop-blur-sm rounded-2xl overflow-hidden">
                              <CardHeader className="p-5 pb-3">
                                <div className="flex justify-between items-start gap-2">
                                  <CardTitle className="text-lg font-black tracking-tight group-hover:text-primary transition-colors">
                                    {hrac.cele_meno}
                                  </CardTitle>
                                  {cisloDresu !== null && (
                                    <span className="text-2xl font-black text-muted-foreground/10 group-hover:text-primary/10 transition-colors select-none">
                                      #{cisloDresu}
                                    </span>
                                  )}
                                </div>
                                <CardDescription className="text-sm font-bold text-primary/80 tracking-wide uppercase">
                                  {hrac.klub}
                                </CardDescription>
                              </CardHeader>
                              <CardContent className="p-5 pt-0 flex items-center justify-between">
                                <div className="flex flex-col gap-1">
                                  <Badge
                                    variant="outline"
                                    className="text-[10px] font-black uppercase tracking-widest bg-muted/20 border-muted/30 w-fit"
                                  >
                                    {hrac.sutaz}
                                  </Badge>
                                  {hrac.sezona_zapasy > 0 && (
                                    <span className="text-[10px] font-bold text-muted-foreground/80">
                                      {hrac.sezona_zapasy}z ({hrac.sezona_goly}+
                                      {hrac.sezona_asistencie})
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0">
                                  <span className="text-[10px] text-muted-foreground font-black uppercase tracking-tighter">
                                    Detail
                                  </span>
                                  <div className="p-1.5 bg-primary/10 rounded-full">
                                    <Info className="h-3.5 w-3.5 text-primary" />
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </DialogTrigger>
                          <DialogContent
                            showCloseButton={false}
                            className="w-full lg:w-[94vw] lg:max-w-[94vw] 2xl:max-w-[1400px] h-[90vh] max-h-[90vh] border-muted/20 shadow-2xl p-0 overflow-hidden text-foreground bg-background/95 backdrop-blur-xl flex flex-col m-auto"
                          >
                            <DialogTitle className="sr-only">
                              {hrac.cele_meno} - {PLAYER_DETAIL_TEXT}
                            </DialogTitle>
                            <DialogDescription className="sr-only">
                              Detailné informácie o hráčovi {hrac.cele_meno}
                            </DialogDescription>

                            {/* Sticky Header with Close Button */}
                            <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b border-muted/10 min-h-[60px] shrink-0">
                              <div className="flex items-center justify-between gap-3 px-4 py-3">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div className="p-1.5 bg-background border border-muted/20 shadow-sm rounded-2xl overflow-hidden shrink-0">
                                    <Flag
                                      kod={krajina.kod}
                                      size={32}
                                      className="w-8 h-8"
                                    />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <h2 className="text-lg font-black tracking-tight truncate">
                                      {hrac.cele_meno}
                                    </h2>
                                    <p className="text-xs text-muted-foreground font-bold truncate">
                                      {hrac.klub} • {hrac.sutaz}
                                    </p>
                                  </div>
                                </div>
                                <DialogClose asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-10 w-10 p-0 rounded-full hover:bg-muted/50 shrink-0 flex items-center justify-center"
                                  >
                                    <X className="h-5 w-5" />
                                    <span className="sr-only">Zavrieť</span>
                                  </Button>
                                </DialogClose>
                              </div>
                            </div>

                            {/* Scrollable Content */}
                            <div className="flex-1 overflow-y-auto overscroll-contain">
                              <div className="pt-6 pb-8 px-4 sm:px-8">
                                <div className="flex flex-wrap items-center gap-2 mb-4">
                                  <Badge
                                    variant="secondary"
                                    className="bg-primary/10 text-primary border-none rounded-full px-4 py-1 text-xs font-black uppercase tracking-widest"
                                  >
                                    {getPoziciaLabel(hrac.pozicia)}
                                  </Badge>
                                  {hrac.strana_hokejky && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs font-bold rounded-full px-4 py-1 border-muted/30 bg-muted/5"
                                    >
                                      {hrac.strana_hokejky === "L"
                                        ? "Ľavák"
                                        : "Pravák"}
                                    </Badge>
                                  )}
                                </div>

                                {/* Main Content Grid */}
                                <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
                                  {/* Left Column - Photo Placeholder */}
                                  <div className="flex justify-center lg:justify-start">
                                    <div className="w-48 h-48 sm:w-56 sm:h-56 lg:w-full lg:h-auto lg:aspect-square bg-muted/20 border-2 border-dashed border-muted/40 rounded-2xl flex flex-col items-center justify-center">
                                      <User className="h-16 w-16 text-muted-foreground/40 mb-3" />
                                      <p className="text-sm text-muted-foreground/60 text-center px-4">
                                        Fotografia hráča
                                        <br />
                                        <span className="text-xs">
                                          (plánované)
                                        </span>
                                      </p>
                                    </div>
                                  </div>

                                  {/* Right Column - All Information */}
                                  <div className="space-y-6">
                                    {/* Stats Section */}
                                    <div>
                                      <div className="flex items-center gap-2 mb-3 border-b border-muted/20 pb-2">
                                        <Trophy className="w-4 h-4 text-primary" />
                                        <h3 className="text-sm font-black uppercase tracking-widest">
                                          Sezóna 25/26
                                        </h3>
                                      </div>
                                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        <div className={STAT_CARD_CLASSES}>
                                          <p className="text-[10px] uppercase font-black text-muted-foreground/60 mb-1.5 tracking-widest">
                                            Zápasy
                                          </p>
                                          <p className="text-2xl font-black tracking-tighter">
                                            {hrac.sezona_zapasy || 0}
                                          </p>
                                        </div>
                                        <div className={STAT_CARD_CLASSES}>
                                          <p className="text-[10px] uppercase font-black text-muted-foreground/60 mb-1.5 tracking-widest">
                                            Góly
                                          </p>
                                          <p className="text-2xl font-black tracking-tighter text-status-success">
                                            {hrac.sezona_goly || 0}
                                          </p>
                                        </div>
                                        <div className={STAT_CARD_CLASSES}>
                                          <p className="text-[10px] uppercase font-black text-muted-foreground/60 mb-1.5 tracking-widest">
                                            Asistencie
                                          </p>
                                          <p className="text-2xl font-black tracking-tighter text-status-info">
                                            {hrac.sezona_asistencie || 0}
                                          </p>
                                        </div>
                                        <div className={STAT_CARD_CLASSES}>
                                          <p className="text-[10px] uppercase font-black text-muted-foreground/60 mb-1.5 tracking-widest">
                                            Body
                                          </p>
                                          <p className="text-2xl font-black tracking-tighter text-primary">
                                            {hrac.sezona_body || 0}
                                          </p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Info Section */}
                                    <div>
                                      <div className="flex items-center gap-2 mb-3 border-b border-muted/20 pb-2">
                                        <Activity className="w-4 h-4 text-primary" />
                                        <h3 className="text-sm font-black uppercase tracking-widest">
                                          Informácie
                                        </h3>
                                      </div>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div className={INFO_BOX_CLASSES}>
                                          <p className="text-[10px] uppercase font-black text-muted-foreground/60 mb-1.5 tracking-widest">
                                            Pozícia
                                          </p>
                                          <p className="text-sm font-bold flex items-center gap-3">
                                            <span className="p-1.5 bg-primary/10 rounded-lg text-primary">
                                              {getPoziciaIcon(hrac.pozicia)}
                                            </span>
                                            {getPoziciaLabel(hrac.pozicia)}
                                          </p>
                                        </div>
                                        <div className={INFO_BOX_CLASSES}>
                                          <p className="text-[10px] uppercase font-black text-muted-foreground/60 mb-1.5 tracking-widest">
                                            Vek (k {SITE_TITLE} 2026)
                                          </p>
                                          <p className="text-sm font-bold tracking-tight">
                                            {hrac.vek_k_ms
                                              ? `${hrac.vek_k_ms} rokov`
                                              : "—"}
                                          </p>
                                        </div>
                                        <div className={INFO_BOX_CLASSES}>
                                          <p className="text-[10px] uppercase font-black text-muted-foreground/60 mb-1.5 tracking-widest">
                                            Dátum narodenia
                                          </p>
                                          <p className="text-sm font-bold tracking-tight">
                                            {hrac.datum_narodenia || "—"}
                                          </p>
                                        </div>
                                        {hrac.miesto_narodenia && (
                                          <div className={INFO_BOX_CLASSES}>
                                            <p className="text-[10px] uppercase font-black text-muted-foreground/60 mb-1.5 tracking-widest">
                                              Miesto narodenia
                                            </p>
                                            <p className="text-sm font-bold tracking-tight flex items-center gap-2">
                                              <MapPin className="w-3.5 h-3.5 text-primary/60" />
                                              {hrac.miesto_narodenia}
                                            </p>
                                          </div>
                                        )}
                                        {(hrac.vyska_cm ||
                                          hrac.hmotnost_kg) && (
                                          <div className={INFO_BOX_CLASSES}>
                                            <p className="text-[10px] uppercase font-black text-muted-foreground/60 mb-1.5 tracking-widest">
                                              Parametre
                                            </p>
                                            <p className="text-sm font-bold tracking-tight flex items-center gap-4">
                                              {hrac.vyska_cm && (
                                                <span className="flex items-center gap-1.5">
                                                  <Ruler className="w-3.5 h-3.5 text-primary/60" />
                                                  {hrac.vyska_cm} cm
                                                </span>
                                              )}
                                              {hrac.hmotnost_kg && (
                                                <span className="flex items-center gap-1.5">
                                                  <Scale className="w-3.5 h-3.5 text-primary/60" />
                                                  {hrac.hmotnost_kg} kg
                                                </span>
                                              )}
                                            </p>
                                          </div>
                                        )}
                                        <div className={INFO_BOX_CLASSES}>
                                          <p className="text-[10px] uppercase font-black text-muted-foreground/60 mb-1.5 tracking-widest">
                                            Súťaž
                                          </p>
                                          <p className="text-sm font-bold tracking-tight">
                                            {hrac.sutaz || "—"}
                                          </p>
                                        </div>
                                        <div
                                          className={cn(
                                            INFO_BOX_CLASSES,
                                            "sm:col-span-2",
                                          )}
                                        >
                                          <p className="text-[10px] uppercase font-black text-muted-foreground/60 mb-1.5 tracking-widest">
                                            {hrac.zmluva_info
                                              ? "Zmluva a záväzok"
                                              : "Hodnota / Príjem"}
                                          </p>
                                          <p className="text-sm font-bold tracking-tight leading-relaxed">
                                            {hrac.zmluva_info || (
                                              <>
                                                {hrac.hodnota_hraca
                                                  ? `${hrac.hodnota_hraca.toLocaleString()} $`
                                                  : "—"}{" "}
                                                /{" "}
                                                {hrac.prijem
                                                  ? `${hrac.prijem.toLocaleString()} $`
                                                  : "—"}
                                              </>
                                            )}
                                          </p>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Career Section */}
                                    {(hrac.kariera_prehlad ||
                                      hrac.reprezentacne_skusenosti) && (
                                      <div>
                                        <div className="flex items-center gap-2 mb-3 border-b border-muted/20 pb-2">
                                          <History className="w-4 h-4 text-primary" />
                                          <h3 className="text-sm font-black uppercase tracking-widest">
                                            Kariéra
                                          </h3>
                                        </div>
                                        <div className="space-y-3">
                                          {hrac.kariera_prehlad && (
                                            <div className="space-y-1.5">
                                              <h4 className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                                                Klubová kariéra
                                              </h4>
                                              <p className="text-sm text-muted-foreground/90 leading-relaxed font-medium">
                                                {hrac.kariera_prehlad}
                                              </p>
                                            </div>
                                          )}
                                          {hrac.reprezentacne_skusenosti && (
                                            <div className="space-y-1.5">
                                              <h4 className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                                                Reprezentácia
                                              </h4>
                                              <p className="text-sm text-muted-foreground/90 leading-relaxed font-medium">
                                                {hrac.reprezentacne_skusenosti}
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="mt-6 pt-5 border-t border-muted/10 flex justify-center sm:justify-end">
                                  <Button
                                    asChild
                                    variant="default"
                                    size="lg"
                                    className="rounded-full px-10 h-14 font-black uppercase tracking-widest shadow-xl hover:shadow-2xl transition-all w-full sm:w-auto bg-primary text-primary-foreground hover:scale-105 active:scale-95"
                                  >
                                    <Link
                                      href={
                                        aktivnehlasovanie_id
                                          ? `/hlasovanie/${aktivnehlasovanie_id}`
                                          : "#"
                                      }
                                    >
                                      {VOTE_FOR_PLAYER_TEXT}
                                    </Link>
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </motion.section>
            ))}
          </div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Card className={EMPTY_STATE_CLASSES}>
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
                <User className="h-8 w-8 text-muted-foreground/40" />
              </div>
              <CardTitle className="text-2xl mb-4">
                {NOMINATION_PREPARING_TITLE}
              </CardTitle>
              <CardDescription className="text-lg mb-8 max-w-md mx-auto">
                Oficiálna nominácia pre krajinu {krajina.nazov} zatiaľ nie je v
                našej databáze dostupná. Skúste to neskôr.
              </CardDescription>
              <Button
                asChild
                variant="outline"
                className="rounded-full px-8 border-muted-foreground/20 hover:bg-muted/50"
              >
                <Link href="/skupiny">{VIEW_OTHER_TEAMS_TEXT}</Link>
              </Button>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
